import React, { ReactNode, useCallback, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { societyBoardCreateSchema } from '../../../../forms/schemas/society_board_create'
import { BoardRole } from '../../../../types/board-roles'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import {
  isUserAdminInSociety,
  translateRole,
} from '../../../../helpers/society'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { useUserRole } from '../../../../hooks/useUserRole'
// eslint-disable-next-line max-len
import { useNonBoardMemberResidentUsersOptions } from '../../../../hooks/useNonBoardMemberResidentUsersOptions'
import { FormSpacing } from '../../../common/FormSpacing'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { useAuth } from '../../../../hooks/useAuth'
import { userQueries } from '../../../../api/users'

interface CreateUpdateBoardMemberFormProps {
  onError: (error: unknown) => void
  onSubmit: (data: InferType<typeof societyBoardCreateSchema>) => Promise<void>
  society: ISocietiesModel
  onClose: () => void
  loading: boolean
  userId?: string
}

export const CreateUpdateBoardMemberForm = ({
  userId,
  loading,
  onError,
  onSubmit,
  society,
  onClose,
}: CreateUpdateBoardMemberFormProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()
  const userRole = useUserRole(society, userId)
  const nonBoardMemberResidentUsersOptions =
    useNonBoardMemberResidentUsersOptions(society)
  const { userId: authUserId } = useAuth()

  const updateMode = !!userId
  const { data: user } = useQuery(userQueries.getUser(userId))

  const isAdmin = isUserAdminInSociety(society, authUserId as string)

  if (
    !isAdmin ||
    (updateMode && user === undefined) ||
    (updateMode && userRole === undefined)
  ) {
    onClose()
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const getDefaultValues = useCallback((): InferType<
    typeof societyBoardCreateSchema
  > => {
    return {
      role: updateMode ? (userRole as string) : BoardRole.MEMBER,
      userId: updateMode ? user?._id || '' : '',
    }
  }, [updateMode, user, userRole])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyBoardCreateSchema),
    defaultValues: getDefaultValues(),
  })

  const boardRoleOptions = useMemo(
    () =>
      Object.values(BoardRole).map((val) => ({
        value: val as string,
        label: translateRole(val) as string | '',
      })),
    []
  )

  return (
    <FormSpacing>
      <Controller
        control={control}
        render={({ field: { onChange, value, name } }) => (
          <FormControl
            label={translate('createUpdateBoardMemberForm.labels.role')}
            name={name}
            error={errors.role && getErrorMessage(errors.role)}
          >
            <SelectDropdown
              options={boardRoleOptions}
              value={boardRoleOptions.find((option) => option.value === value)}
              onChange={(option) => onChange(option?.value)}
              error={!!errors.role}
            />
          </FormControl>
        )}
        name="role"
      />
      {!updateMode && (
        <Controller
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <FormControl
              label={translate('createUpdateBoardMemberForm.labels.user')}
              name={name}
              error={errors.userId && getErrorMessage(errors.userId)}
            >
              <SelectDropdown
                options={nonBoardMemberResidentUsersOptions}
                value={nonBoardMemberResidentUsersOptions.find(
                  (option) => option.value === value
                )}
                onChange={(option) => onChange(option?.value)}
                isSearchable
                error={!!errors.userId}
              />
            </FormControl>
          )}
          name="userId"
        />
      )}
      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
        <Button
          label={translate('common.actions.cancel')}
          onClick={onClose}
          variant={ButtonVariant.TEXT}
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || loading}
          label={translate(
            updateMode ? 'common.actions.save' : 'common.actions.add'
          )}
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </FormSpacing>
  )
}
